import { handleKeyboardClick, sleep } from "@/utils";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import clsx from "clsx";
import {
	JSX,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

interface PositionInfo {
	left: number;
	top: number;
}

interface MenuShellProps {
	children: ReactNode;
	className?: string;
	isClosing: boolean;
	isOpen: boolean;
	onClose: () => void;
	x: number;
	y: number;
}

function MenuShell({
	children,
	className,
	isClosing,
	isOpen,
	onClose,
	x,
	y,
}: MenuShellProps): JSX.Element {
	const [position, setPosition] = useState<PositionInfo | null>(null);
	const [transformOrigin, setTransformOrigin] = useState<string>("");
	const menuRef = useRef<HTMLDivElement>(null);

	const calculatePosition = useCallback(async (): Promise<void> => {
		await sleep(0); // avoid menuRef.current being null
		if (!isOpen || position || !menuRef.current) {
			return;
		}
		const menuRect = menuRef.current.getBoundingClientRect();
		if (!menuRect) {
			return;
		}
		const { width, height } = menuRect;
		const windowWidth = window.innerWidth;
		const windowHeight = window.innerHeight;

		let left = x - width / 2;
		let top = y + 10;

		// Handle horizontal overflow
		if (left < 0) {
			left = 10;
		} else if (left + width > windowWidth) {
			left = windowWidth - width - 10;
		}

		// Handle vertical overflow
		if (top + height > windowHeight) {
			top = y - height - 10;
		}

		setPosition({ left, top });
		setTransformOrigin(
			`${left > x ? "right" : "left"} ${top > y ? "bottom" : "top"}`,
		);
	}, [position, isOpen, x, y]);

	useEffect(() => {
		void calculatePosition();
	}, [calculatePosition]);

	useEffect(() => {
		if (!isOpen) {
			setPosition(null);
			setTransformOrigin("");
		}
	}, [isOpen]);

	if (!isOpen) {
		return <></>;
	}

	return (
		<Dialog
			onClose={onClose}
			open={isOpen}
		>
			<DialogBackdrop
				className={clsx(
					"overlay",
					!isClosing && "fade-in",
					(isClosing || !isOpen) && "fade-out",
				)}
				role="button"
				tabIndex={0}
				onClick={onClose}
				onKeyDown={handleKeyboardClick(onClose)}
			/>
			<DialogPanel
				ref={menuRef}
				className={clsx(
					className,
					!isClosing && "fade-in",
					(isClosing || !isOpen) && "fade-out",
				)}
				style={{
					left: position?.left ?? x,
					top: position?.top ?? y,
					transformOrigin,
				}}
			>
				{children}
			</DialogPanel>
		</Dialog>
	);
}

export default MenuShell;
